import { Link, useParams } from "react-router-dom";
import { useChangelogById, useConfig } from "../hooks/useApi";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  ArrowLeft,
  Copy,
  Check,
  Link as LinkIcon,
  Tag as TagIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useSeoMeta, summaryToDescription } from "../hooks/useSeoMeta";
import { formatDate, formatRelativeDate, formatMarkdownForWhatsApp } from "../utils";
import toast from "react-hot-toast";
import { ChangelogMarkdown } from "../components/ChangelogMarkdown";

export function ChangelogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: release, isLoading, error } = useChangelogById(id);
  const { data: config } = useConfig();
  const [hasCopied, setHasCopied] = useState(false);
  const [hasCopiedLink, setHasCopiedLink] = useState(false);

  const repoLabel = config
    ? config.repository.displayName ||
      `${config.repository.owner}/${config.repository.name}`
    : "este projeto";
  const brandName = config?.branding?.name || "Changelog";

  const description = useMemo(
    () => (release ? summaryToDescription(release.summary) : undefined),
    [release]
  );

  const canonicalPath = release
    ? `/changelog/${encodeURIComponent(release.tagName)}`
    : undefined;

  const ogImage = release
    ? `${window.location.origin}/og/release/${encodeURIComponent(release.tagName)}.png`
    : undefined;

  const jsonLd = useMemo(
    () =>
      release
        ? {
            "@context": "https://schema.org",
            "@type": "TechArticle",
            headline: `${release.tagName} · Notas de versão`,
            name: `${release.tagName} · Notas de versão`,
            description: description || `Notas de versão ${release.tagName}.`,
            datePublished: release.releaseDate,
            url: `${window.location.origin}${canonicalPath}`,
            inLanguage: "pt-BR",
            image: ogImage,
            about: { "@type": "SoftwareApplication", name: repoLabel },
            author: {
              "@type": "Organization",
              name: brandName,
            },
            publisher: {
              "@type": "Organization",
              name: brandName,
            },
          }
        : null,
    [release, description, repoLabel, canonicalPath, ogImage, brandName]
  );

  useSeoMeta({
    title: release
      ? `${release.tagName} · ${brandName} · ${repoLabel}`
      : `Carregando · ${brandName}`,
    description: description || `Notas de versão de ${repoLabel}.`,
    canonicalPath,
    ogType: "article",
    ogImage,
    publishedTime: release?.releaseDate,
    jsonLd,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <LoadingSpinner size="lg" text="" />
      </div>
    );
  }

  if (error || !release) {
    return (
      <div className="py-16 max-w-md">
        <h1 className="font-display text-3xl font-semibold mb-3 text-foreground">
          Changelog não encontrado
        </h1>
        <p className="mb-6 text-muted-foreground leading-relaxed">
          O changelog solicitado não existe ou foi removido.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-primary hover:underline underline-offset-4"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Voltar para todas as versões
        </Link>
      </div>
    );
  }

  const handleCopy = async () => {
    const whatsappText = formatMarkdownForWhatsApp(release.summary);
    const text = `*${release.tagName}*\n\n${whatsappText}`;
    try {
      await navigator.clipboard.writeText(text);
      setHasCopied(true);
      toast.success("Copiado");
      setTimeout(() => setHasCopied(false), 2000);
    } catch {
      toast.error("Erro ao copiar");
    }
  };

  const handleShareLink = async () => {
    const link = `${window.location.origin}${canonicalPath}`;
    try {
      await navigator.clipboard.writeText(link);
      setHasCopiedLink(true);
      toast.success("Link copiado");
      setTimeout(() => setHasCopiedLink(false), 2000);
    } catch {
      toast.error("Erro ao copiar");
    }
  };

  const lines = release.summary.split("\n").filter((l) => l.trim());
  const lead = lines[1] && !lines[1].startsWith("#") && !lines[1].startsWith("-") ? lines[1] : "";

  return (
    <article>
      <nav aria-label="Migalhas de pão" className="mb-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors group"
        >
          <ArrowLeft
            className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform"
            aria-hidden="true"
          />
          Todas as versões
        </Link>
      </nav>

      <header className="mb-10 pb-8 border-b border-border relative">
        <span
          aria-hidden="true"
          className="absolute -left-3 top-1 bottom-8 w-0.5 rounded-full bg-primary/40 hidden sm:block"
        />
        <p className="text-xs uppercase tracking-[0.18em] text-primary font-medium mb-4 font-mono inline-flex items-center gap-2">
          <TagIcon className="w-3 h-3" aria-hidden="true" />
          <span>Versão · {repoLabel}</span>
        </p>
        <h1 className="font-display text-5xl sm:text-6xl font-semibold text-foreground leading-[1.04] tracking-tight font-mono">
          {release.tagName}
        </h1>
        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <time dateTime={release.releaseDate} title={formatDate(release.releaseDate)}>
            Publicada {formatRelativeDate(release.releaseDate)}
          </time>
          {release.previousTagName && (
            <span>
              Comparando com{" "}
              <span className="font-mono text-primary/80">{release.previousTagName}</span>
            </span>
          )}
        </div>
        {lead && (
          <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{lead}</p>
        )}
      </header>

      <ChangelogMarkdown className="text-base">{release.summary}</ChangelogMarkdown>

      <footer className="mt-12 pt-6 border-t border-border flex flex-wrap items-center gap-6 text-sm">
        <button
          onClick={handleShareLink}
          className="inline-flex items-center gap-2 font-medium text-muted-foreground hover:text-primary transition-colors"
        >
          {hasCopiedLink ? (
            <Check className="w-4 h-4 text-primary" aria-hidden="true" />
          ) : (
            <LinkIcon className="w-4 h-4" aria-hidden="true" />
          )}
          {hasCopiedLink ? "Link copiado" : "Compartilhar link"}
        </button>
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-2 font-medium text-muted-foreground hover:text-primary transition-colors"
        >
          {hasCopied ? (
            <Check className="w-4 h-4 text-primary" aria-hidden="true" />
          ) : (
            <Copy className="w-4 h-4" aria-hidden="true" />
          )}
          {hasCopied ? "Copiado" : "Copiar para WhatsApp"}
        </button>
      </footer>
    </article>
  );
}
